import type { Contact } from '@/api/queries/conversations';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ContactItem } from './ContactItem';
import { useContactFilter } from './useContactFilter';

interface ContactListProps {
  onContactClick: (contact: Contact) => void;
  showEditButtons?: boolean;
  addContactButton?: React.ReactNode;
  disabled?: boolean;
}

export function ContactList({
  onContactClick,
  showEditButtons = false,
  addContactButton,
  disabled = false,
}: ContactListProps) {
  const { t } = useTranslation('conversations');
  const { filteredContacts, searchTerm, setSearchTerm, isLoading } = useContactFilter();

  const handleContactClick = (contact: Contact) => {
    onContactClick(contact);
  };

  return (
    <div className="h-full">
      {/* Search and Add Contact */}
      <div className="mb-2 flex gap-2">
        <div className="relative flex-1">
          <Input
            type="text"
            placeholder={t('contactList.searchContacts')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            data-testid="contact-list-search"
            disabled={disabled}
          />
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
        </div>
        {addContactButton && <div className="flex items-center">{addContactButton}</div>}
      </div>

      {/* Contact List */}
      <div className="overflow-y-auto">
        {isLoading ? (
          <div className="p-6 text-center text-gray-500" data-testid="contact-list-loading">
            {t('contactList.loadingContacts')}
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="p-6 text-center text-gray-500" data-testid="contact-list-empty">
            {searchTerm ? t('contactList.noContactsFound') : t('contactList.noContactsAvailable')}
          </div>
        ) : (
          <div className="divide-y divide-gray-100" data-testid="contact-list-items">
            {filteredContacts.map(contact => (
              <ContactItem
                key={contact.id}
                contact={contact}
                onClick={handleContactClick}
                showEditButtons={showEditButtons}
                disabled={disabled}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
